import { useTranslate } from "@refinedev/core";
import { CarOutlined, DownloadOutlined } from "@ant-design/icons";
import { Tooltip, Button } from "antd";
import { GlassCard, ActionButton } from "../../../shared/components";
import { usePageStyles } from "../../../shared/styles";
import { useColorMode } from "../../../contexts/color-mode";
import type { DashboardOrder, DashboardAttachment } from "../types";

export interface FeaturedVehicleCardProps {
    order: DashboardOrder & { make?: string; model?: string; year?: string; plate?: string; vin?: string; products?: Array<{ name: string }> };
    outAttachment?: DashboardAttachment | null;
}

export function FeaturedVehicleCard({ order, outAttachment }: FeaturedVehicleCardProps) {
    const _t = useTranslate();
    const styles = usePageStyles();
    const { mode } = useColorMode();
    const isDark = mode === "dark";

    return (
        <GlassCard variant="emerald">
            <div className="flex justify-between items-start mb-5">
                <div className="flex items-center gap-3">
                    <div
                        className="w-11 h-11 rounded-2xl flex items-center justify-center border"
                        style={{
                            background: `linear-gradient(135deg, ${styles.primaryRgba(0.2)} 0%, ${styles.primaryRgba(0.05)} 100%)`,
                            borderColor: styles.primaryRgba(0.2),
                        }}
                    >
                        <CarOutlined className="text-[22px]" style={{ color: styles.primaryColor }} />
                    </div>
                    <div>
                        <p className="m-0 text-sm font-semibold" style={{ color: styles.container.color }}>
                            {_t("dashboard.vehicle.lastProcessed", "Dernier véhicule traité")}
                        </p>
                        <p
                            className="m-0 text-xs"
                            style={{
                                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                            }}
                        >
                            {_t("dashboard.vehicle.optimizationComplete", "Optimisation terminée")}
                        </p>
                    </div>
                </div>
                <span
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{
                        background: styles.successRgba(0.1),
                        color: styles.semantic.success,
                        borderColor: styles.successRgba(0.2),
                    }}
                >
                    ✓ {_t("dashboard.vehicle.delivered", "Livré")}
                </span>
            </div>

            <div
                className="rounded-2xl p-5 relative"
                style={{
                    background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                }}
            >
                <div className="flex justify-between items-center">
                    <div>
                        <p
                            className="m-0 mb-1 text-2xl font-bold tracking-tight text-white"
                            style={{ color: styles.container.color }}
                        >
                            {order.make || "Fabricant"} {order.model || "Modèle"}
                        </p>
                        <p
                            className="m-0 text-[13px] font-mono"
                            style={{
                                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                            }}
                        >
                            {order.year || "Année non renseignée"} {order.year && (order.plate || order.vin) ? "•" : ""}{" "}
                            {order.plate || order.vin || "Immatriculation"}
                        </p>
                    </div>
                    {order.products && order.products.length > 0 && (() => {
                        const productName = order.products[0]?.name || "";
                        const truncatedName = productName.length > 20 ? `${productName.slice(0, 20)}...` : productName;
                        return (
                            <Button type="primary" className="h-20">
                                <div>
                                    <p className="m-0 text-[11px] text-blue-100 uppercase tracking-wider font-medium">
                                        {_t("dashboard.vehicle.tuning", "TUNING")}
                                    </p>
                                    {productName.length > 20 ? (
                                        <Tooltip title={productName}>
                                            <p className="m-0 text-sm font-bold text-white mt-0.5 cursor-help">{truncatedName}</p>
                                        </Tooltip>
                                    ) : (
                                        <p className="m-0 text-sm font-bold text-white mt-0.5">{productName}</p>
                                    )}
                                </div>
                            </Button>
                        );
                    })()}
                </div>
            </div>

            {outAttachment?.file?.path && (
                <ActionButton
                    variant="secondary"
                    onClick={() => window.open(outAttachment!.file.path, "_blank")}
                    icon={<DownloadOutlined />}
                    style={{ width: "100%", justifyContent: "center", marginTop: "16px" }}
                >
                    {_t("dashboard.vehicle.downloadFile", "Télécharger le fichier")}
                </ActionButton>
            )}
        </GlassCard>
    );
}
